/**
 * The aerodrome drawing's canvas and rasters.
 *
 * The demo binds these at load — `const cv = document.getElementById("map")` —
 * which cannot work here, because in React the canvas does not exist until the
 * component mounts. So the bindings live in this module and the component
 * attaches to them, and the extracted drawing code reads them by the same names
 * it always did.
 *
 * Three rasters make up the sheet: the scanned aerodrome plan, and two tinted
 * overlays for the taxiway and runway linework. `ready` is what the drawing code
 * checks; before it is true every geometry helper returns a harmless default
 * rather than reading a null image, which is the state of the first second of
 * every session.
 */

export let cv: HTMLCanvasElement | null = null;
export let ctx: CanvasRenderingContext2D | null = null;

export let base: HTMLImageElement | null = null;
export let ovTaxi: HTMLCanvasElement | null = null;
export let ovRwy: HTMLCanvasElement | null = null;

/*
 * `ready`, `night` and `selected` are NOT owned here.
 *
 * The extracted slice assigns to all three — `selected = {kind:"wo", o}` when a
 * row is clicked, and so on — and an ES import cannot be assigned to. Dev mode
 * tolerates it; the production bundler rejects it outright, which is the honest
 * answer. They live in the slice, which exports a setter for each.
 */
export let ready = false;

/*
 * Pan and zoom is NOT here. `fit()` reassigns it wholesale — `view = {k, x, y}`
 * — and an imported binding cannot be reassigned, so the generated slice
 * declares and exports it instead. Read it from domain/generated.
 */

/**
 * The sheet is a portrait A0. Screens are not. 90 turns the plan a quarter turn
 * for display only — the georeferencing underneath is untouched, so every
 * coordinate, grid section and report reads exactly the same.
 */
export let ROT = 90;
export const setROT = (deg: number) => {
  ROT = deg;
};

/** Which layers are drawn. The chips along the top toggle these. */
export const layers: Record<string, boolean> = {
  grid: true,
  load: false,
  rwy: true,
  taxi: true,
  stands: true,
  holds: true,
  agl: false,
  nav: true,
  signs: false,
  wos: true,
  heat: false,
  active: true,
  tdz: true,
  fric: true,
  surf: true,
  infra: true,
  rff: true,
  bldg: true,
  fod: false,
  fleet: true,
  fricpin: false,
};

/**
 * The legend panel. `renderChips()` writes its markup, so React must not also
 * render into it — the component provides the empty element and delegates
 * clicks, exactly as the demo does.
 */
export let chipBox: HTMLElement | null = null;
export const attachChips = (el: HTMLElement | null) => {
  chipBox = el;
};

export function attachCanvas(el: HTMLCanvasElement | null): void {
  cv = el;
  ctx = el ? el.getContext("2d") : null;
}

/** Recolour a mask so the linework can be drawn over the scan in one colour. */
export function tinted(img: HTMLImageElement, colour: string): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = img.width;
  c.height = img.height;
  const g = c.getContext("2d")!;
  g.drawImage(img, 0, 0);
  g.globalCompositeOperation = "source-in";
  g.fillStyle = colour;
  g.fillRect(0, 0, c.width, c.height);
  return c;
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = () => rej(new Error(`could not load ${src}`));
    i.src = src;
  });

let loading: Promise<void> | null = null;

/**
 * Load the three rasters once per session, however many components ask. The
 * base is 2 MB, so a second request while the first is in flight waits on it
 * rather than starting again.
 */
export function loadRasters(basePath = "/assets"): Promise<void> {
  if (ready) return Promise.resolve();
  if (loading) return loading;
  loading = (async () => {
    const [b, t, rw] = await Promise.all([
      loadImage(`${basePath}/airfield-base.jpg`),
      loadImage(`${basePath}/overlay-taxiways.png`),
      loadImage(`${basePath}/overlay-runways.png`),
    ]);
    base = b;
    ovTaxi = tinted(t, "#F2C230");
    ovRwy = tinted(rw, "#F2F5F7");
    ready = true;
  })();
  return loading;
}

/*
 * The realistic sheet. "off" draws the survey scan; "day" and "night" paint
 * the airfield as it would look from above, from the same records, in
 * src/demo/domain/realMap.ts. The drawing code asks `REAL` and calls
 * `paintReal` at the point where it would otherwise put the scan.
 */
import { paintReal as paintRealSheet, type RealMode } from "./realMap";

export let REAL: RealMode = "off";
export const setREAL = (v: RealMode) => {
  REAL = v;
};

export function paintReal(g: CanvasRenderingContext2D, W: number, H: number): boolean {
  if (REAL === "off") return false;
  return paintRealSheet(g, W, H, REAL, performance.now());
}

/*
 * The derived taxiway centrelines and apron outlines, for the engine's road
 * network. Built by scripts/build-pavement.mjs from the survey overlays and
 * shipped in agl-derived.json; the network adds them as chains so a dispatched
 * vehicle drives the pavement rather than a line between holding positions.
 */
import derived from "../../data/agl-derived.json";

export function extraChains(): { pts: [number, number][]; kind: "taxiway" | "apron" }[] {
  const d = derived as unknown as { twyChains?: [number, number][][]; aprons?: [number, number][][] };
  const twy = (d.twyChains ?? []).filter((c) => c.length >= 2).map((pts) => ({ pts, kind: "taxiway" as const }));
  const aprons = (d.aprons ?? []).filter((c) => c.length >= 3).map((pts) => ({ pts: [...pts, pts[0]], kind: "apron" as const }));
  return [...twy, ...aprons];
}
